import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";

interface Blog {
  id: number;
  title: string;
  category_name: string;
  tags: string[];
  created_at: string;
}

const ListBlogs = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);

  const navigate = useNavigate();

  // Récupération du token
  const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // 1. Fetch Blogs
  const fetchBlogs = async (page = 1) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/blogs?page=${page}`, config_token);
      setBlogs(response.data.data);
      setCurrentPage(response.data.current_page || 1);
      setLastPage(response.data.last_page || 1);
    } catch (error) {
      console.error("Error fetching blogs:", error);
      toast.error("Error fetching blogs");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // 2. Filtrer les blogs (recherche locale sur la page courante)
  const filteredBlogs = useMemo(() => {
    if (!searchTerm) return blogs;
    const term = searchTerm.toLowerCase();
    return blogs.filter(
      (blog) =>
        blog.title.toLowerCase().includes(term) ||
        blog.category_name.toLowerCase().includes(term) ||
        blog.tags?.some((tag) => tag.toLowerCase().includes(term))
    );
  }, [blogs, searchTerm]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= lastPage) {
      fetchBlogs(page);
    }
  };

  const getPaginationRange = (current: number, total: number) => {
    const delta = 1;
    const range: (number | string)[] = [];
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      } else if (range[range.length - 1] !== "...") {
        range.push("...");
      }
    }
    return range;
  };

  // 3. Actions Handlers
  const handleDeleteClick = (blog: Blog) => {
    setSelectedBlog(blog);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedBlog) return;
    try {
      await axios.delete(`${API_URL}/blogs/${selectedBlog.id}`, config_token);
      toast.success("Blog deleted successfully");
      setBlogs((prev) => prev.filter((b) => b.id !== selectedBlog.id));
    } catch (error: any) {
      console.error("Error deleting blog:", error.response || error.message);
      toast.error("Error deleting blog");
    } finally {
      setShowDeleteModal(false);
      setSelectedBlog(null);
    }
  };

  const handleEditClick = (id: number) => {
    navigate(`/admin/edit-blog/${id}`);
  };

  const clearSearch = () => setSearchTerm("");

  // ── Skeleton Loader ─────────────────────────────────────────────────────────
  function SkeletonLoader() {
    return (
      <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "20px 20px", boxSizing: "border-box" }}>
        <style>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>

        {/* Header Skeleton */}
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <div style={{
                width: 180,
                height: 28,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 6,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{
                width: 60,
                height: 20,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 12,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
            <div style={{
              width: 280,
              height: 18,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 4,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
          <div style={{
            width: 150,
            height: 42,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
            backgroundSize: "200% 100%",
            borderRadius: 8,
            animation: "shimmer 1.5s infinite"
          }}></div>
        </div>

        {/* Main Card Skeleton */}
        <div style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          marginTop: "15px",
        }}>
          {/* Filter Bar Skeleton */}
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #edf2f7" }}>
            <div className="row g-3 align-items-center">
              <div className="col-12 col-md-6 col-lg-5">
                <div style={{
                  width: "100%",
                  height: 42,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 8,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
            </div>
          </div>

          {/* Table Header Skeleton */}
          <div style={{ padding: "12px 16px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <div className="row">
              {["Title", "Category", "Tags", "Date Created", "Actions"].map((_, i) => (
                <div key={i} className="col" style={{ padding: "0 8px" }}>
                  <div style={{
                    width: i === 0 ? 100 : i === 1 ? 80 : i === 2 ? 100 : i === 3 ? 80 : 60,
                    height: 14,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                </div>
              ))}
            </div>
          </div>

          {/* Table Rows Skeleton */}
          {[...Array(8)].map((_, rowIndex) => (
            <div
              key={rowIndex}
              style={{
                padding: "14px 16px",
                borderBottom: rowIndex < 7 ? "1px solid #f1f5f9" : "none",
                display: "flex",
                alignItems: "center"
              }}
            >
              {[...Array(5)].map((_, colIndex) => (
                <div key={colIndex} style={{ flex: 1, padding: "0 8px" }}>
                  <div style={{
                    width: colIndex === 0 ? 160 : colIndex === 1 ? 80 : colIndex === 2 ? 160 : colIndex === 3 ? 100 : 70,
                    height: colIndex === 0 ? 16 : 14,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                  {colIndex === 2 && (
                    <div style={{
                      width: 60,
                      height: 20,
                      marginTop: 4,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                      backgroundSize: "200% 100%",
                      borderRadius: 12,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                  )}
                </div>
              ))}
            </div>
          ))}

          {/* Pagination Skeleton */}
          <div style={{
            padding: "12px 20px",
            borderTop: "1px solid #edf2f7",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <div style={{
              width: 120,
              height: 16,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 4,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{ display: "flex", gap: "6px" }}>
              {[...Array(5)].map((_, i) => (
                <div key={i} style={{
                  width: i === 0 || i === 4 ? 70 : 32,
                  height: 32,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 6,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        padding: "20px 20px",
        boxSizing: "border-box",
        color: "#1e293b",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* SECTION EN-TÊTE */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h1
              style={{
                color: "#294b5a",
                fontSize: "22px",
                fontWeight: 700,
                margin: 0,
                letterSpacing: "-0.02em",
              }}
            >
              Manage Blogs
            </h1>
            <span
              style={{
                backgroundColor: "#294b5a",
                color: "#d3be76",
                fontSize: "11px",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "12px",
                letterSpacing: "0.5px",
              }}
            >
              System
            </span>
          </div>
          <p
            style={{
              color: "#64748b",
              fontSize: "13.5px",
              margin: 0,
              fontWeight: 400,
            }}
          >
            Overview and management of{" "}
            <strong style={{ color: "#294b5a" }}>{filteredBlogs.length}</strong>{" "}
            of <strong>{blogs.length}</strong> blogs on this page.
          </p>
        </div>

        <button
          onClick={() => navigate("/admin/add-blog")}
          style={{
            backgroundColor: "#294b5a",
            color: "#ffffff",
            border: "none",
            borderRadius: "8px",
            padding: "10px 18px",
            fontSize: "13.5px",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "0 2px 8px rgba(41, 75, 90, 0.25)",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "#1e3743";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(41, 75, 90, 0.35)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "#294b5a";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(41, 75, 90, 0.25)";
          }}
        >
          <i className="fa-solid fa-plus" style={{ color: "#fff", fontSize: "13px" }}></i>
          <span>Add New Blog</span>
        </button>
      </div>

      {/* BLOC PRINCIPAL CONTENANT FILTRES + TABLE */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 16px -2px rgba(0,0,0,0.03)",
          overflow: "hidden",
          marginTop: "15px",
        }}
      >
        {/* BARRE DE FILTRES */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #edf2f7",
            backgroundColor: "#ffffff",
          }}
        >
          <div className="row g-3 align-items-center">
            <div className="col-12 col-md-6 col-lg-5">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder="Search blogs by title, category, tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px 36px 9px 38px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "13px",
                    outline: "none",
                    color: "#0f172a",
                    backgroundColor: "#f8fafc",
                    transition: "all 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#294b5a";
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.boxShadow = "0 0 0 3px rgba(41, 75, 90, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#cbd5e1";
                    e.target.style.backgroundColor = "#f8fafc";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "13px",
                    color: "#94a3b8",
                  }}
                ></i>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#94a3b8",
                      padding: "0",
                    }}
                  >
                    <i className="fa-solid fa-xmark" style={{ fontSize: "14px" }}></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* TABLEAU */}
        {isLoading ? (
          <SkeletonLoader />
        ) : (
          <>
            <div className="table-responsive" style={{ margin: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Title</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Category</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Tags</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Date Created</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px", textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBlogs.length > 0 ? (
                    filteredBlogs.map((blog) => (
                      <tr
                        key={blog.id}
                        style={{ borderBottom: "1px solid #f1f5f9", transition: "background-color 0.15s ease" }}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#fafaf8")}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <td style={{ padding: "14px 16px", fontSize: "13.5px", fontWeight: 600, color: "#0f172a" }}>
                          {blog.title}
                        </td>

                        <td style={{ padding: "14px 16px" }}>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "3px 10px",
                              borderRadius: "20px",
                              fontSize: "12px",
                              fontWeight: 600,
                              backgroundColor: "#f0fdf4",
                              color: "#15803d",
                              border: "1px solid #bbf7d0",
                            }}
                          >
                            {blog.category_name}
                          </span>
                        </td>

                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                            {blog.tags && blog.tags.length > 0 ? (
                              blog.tags.map((tag, i) => (
                                <span
                                  key={i}
                                  style={{
                                    padding: "3px 10px",
                                    borderRadius: "20px",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    backgroundColor: "#eff6ff",
                                    color: "#1e40af",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {tag}
                                </span>
                              ))
                            ) : (
                              <span style={{ fontSize: "12.5px", color: "#94a3b8" }}>--</span>
                            )}
                          </div>
                        </td>

                        <td style={{ padding: "14px 16px", fontSize: "13.5px", color: "#475569" }}>
                          {new Date(blog.created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </td>

                        <td style={{ padding: "14px 16px", textAlign: "right" }}>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            <button
                              type="button"
                              onClick={() => handleEditClick(blog.id)}
                              style={{
                                border: "1px solid #e2e8f0",
                                backgroundColor: "#ffffff",
                                color: "#294b5a",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = "#f8fafc";
                                e.currentTarget.style.borderColor = "#cbd5e1";
                                e.currentTarget.style.color = "#d3be76";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffffff";
                                e.currentTarget.style.borderColor = "#e2e8f0";
                                e.currentTarget.style.color = "#294b5a";
                              }}
                              title="Edit"
                            >
                              <i className="fa-solid fa-pen-to-square" style={{ fontSize: "13px" }}></i>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeleteClick(blog)}
                              style={{
                                border: "1px solid #e2e8f0",
                                backgroundColor: "#ffffff",
                                color: "#64748b",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = "#fef2f2";
                                e.currentTarget.style.borderColor = "#fecaca";
                                e.currentTarget.style.color = "#dc2626";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffffff";
                                e.currentTarget.style.borderColor = "#e2e8f0";
                                e.currentTarget.style.color = "#64748b";
                              }}
                              title="Delete"
                            >
                              <i className="fa-solid fa-trash-can" style={{ fontSize: "13px" }}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center", padding: "48px 20px", color: "#64748b", fontSize: "13.5px" }}>
                        <i
                          className="fa-solid fa-newspaper mb-2"
                          style={{ fontSize: "24px", color: "#cbd5e1", display: "block" }}
                        ></i>
                        {searchTerm ? "No blogs found matching your search." : "No blogs found."}
                        {searchTerm && (
                          <div style={{ marginTop: "8px" }}>
                            <button
                              onClick={clearSearch}
                              style={{
                                background: "none",
                                border: "none",
                                color: "#294b5a",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: 600,
                                textDecoration: "underline",
                              }}
                            >
                              Clear search
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* BARRE DE PAGINATION */}
            {blogs.length > 0 && !searchTerm && lastPage > 1 && (
              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid #edf2f7",
                  backgroundColor: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  Page <strong>{currentPage}</strong> of <strong>{lastPage}</strong>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "6px 12px",
                      fontSize: "12.5px",
                      fontWeight: "600",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      backgroundColor: "#ffffff",
                      color: currentPage === 1 ? "#94a3b8" : "#294b5a",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    Previous
                  </button>

                  {getPaginationRange(currentPage, lastPage).map((item, index) => {
                    if (item === "...") {
                      return (
                        <span
                          key={`dots-${index}`}
                          style={{ padding: "0 6px", fontSize: "13px", color: "#94a3b8", fontWeight: "600", userSelect: "none" }}
                        >
                          ...
                        </span>
                      );
                    }
                    const pageNum = item as number;
                    const isActive = currentPage === pageNum;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        style={{
                          width: "32px",
                          height: "32px",
                          fontSize: "12.5px",
                          fontWeight: "600",
                          borderRadius: "6px",
                          border: isActive ? "none" : "1px solid #cbd5e1",
                          backgroundColor: isActive ? "#294b5a" : "#ffffff",
                          color: isActive ? "#ffffff" : "#334155",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === lastPage}
                    style={{
                      padding: "6px 12px",
                      fontSize: "12.5px",
                      fontWeight: "600",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      backgroundColor: "#ffffff",
                      color: currentPage === lastPage ? "#94a3b8" : "#294b5a",
                      cursor: currentPage === lastPage ? "not-allowed" : "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal: Confirmation de suppression */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
            }}
          >
            <div style={{ padding: "28px 24px 20px 24px", textAlign: "center" }}>
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "16px",
                }}
              >
                <i className="fa-solid fa-trash-can" style={{ color: "#dc2626", fontSize: "22px" }}></i>
              </div>

              <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#294b5a", margin: "0 0 8px 0", letterSpacing: "-0.01em" }}>
                Delete Blog?
              </h2>

              <p style={{ fontSize: "13.5px", color: "#64748b", margin: 0, lineHeight: "1.5" }}>
                Are you sure you want to delete this blog? This action cannot be undone.
              </p>

              {selectedBlog && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px 16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    textAlign: "left",
                  }}
                >
                  <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#0f172a" }}>
                    {selectedBlog.title}
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                    Category: {selectedBlog.category_name || "N/A"}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px 24px 20px 24px", backgroundColor: "#ffffff" }}>
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#64748b",
                  backgroundColor: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                  e.currentTarget.style.color = "#0f172a";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.color = "#64748b";
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmDelete}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#ffffff",
                  backgroundColor: "#dc2626",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(220, 38, 38, 0.25)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#b91c1c";
                  e.currentTarget.style.boxShadow = "0 4px 10px rgba(220, 38, 38, 0.35)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#dc2626";
                  e.currentTarget.style.boxShadow = "0 2px 6px rgba(220, 38, 38, 0.25)";
                }}
              >
                <i className="fa-solid fa-trash-can" style={{ fontSize: "13px" }}></i>
                Delete Blog
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListBlogs;